// components/ChecklistSticky.js
import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import './ChecklistSticky.css';
import { useWindowOperations } from '../hooks/useWindowOperations';
import * as stickiesApi from '../api/stickiesApi';

const ChecklistSticky = ({ 
  sticky, 
  updateSticky, 
  removeSticky, 
  maxZIndex, 
  onFocus,
  onToggleDisplayMode,
  isPinned,
  onPinChange,
  isInMainPanel = false,
  registerWithZManager,
  ...props
}) => {
  const {
    isExpanded,
    isMinimized,
    startDrag,
    startResize,
    toggleMinimize,
    toggleExpand
  } = useWindowOperations({ sticky, updateSticky, onFocus, isInMainPanel });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItemText, setNewItemText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Fetch checklist items from API
  const fetchChecklistItems = async () => {
    if (!sticky.note_id) return;
    
    try {
      setIsLoading(true);
      const items = await stickiesApi.fetchChecklistItems(sticky.note_id, sticky.id);
      setChecklistItems(items);
    } catch (error) {
      console.error('Error fetching checklist items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add new checklist item
  const addChecklistItem = async () => {
    if (!newItemText.trim() || !sticky.note_id) return;

    try {
      const newItem = {
        text: newItemText.trim(),
        completed: false,
        order: checklistItems.length
      };

      const createdItem = await stickiesApi.createChecklistItem(
        sticky.note_id, 
        sticky.id, 
        newItem
      );

      setChecklistItems([...checklistItems, createdItem]);
      setNewItemText('');
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  // Update checklist item (toggle completed)
  const updateChecklistItem = async (itemId, updates) => {
    try {
      const updatedItem = await stickiesApi.updateChecklistItem(
        sticky.note_id,
        sticky.id,
        itemId,
        updates
      );

      setChecklistItems(checklistItems.map(item => 
        item.id === itemId ? updatedItem : item
      ));
    } catch (error) {
      console.error('Error updating checklist item:', error);
    }
  };

  // Delete checklist item
  const deleteChecklistItem = async (itemId) => {
    try {
      await stickiesApi.deleteChecklistItem(
        sticky.note_id,
        sticky.id,
        itemId
      );

      setChecklistItems(checklistItems.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  // Toggle checklist item completion status
  const toggleItemCompleted = (itemId) => {
    const item = checklistItems.find(item => item.id === itemId);
    if (item) {
      const updates = {
        ...item,
        completed: !item.completed
      };
      updateChecklistItem(itemId, updates);
    }
  };

  // Handle pin toggle (prevent sticky auto-minimize)
  const handlePinToggle = (e) => {
    e.stopPropagation();
    if (onPinChange) {
      onPinChange(sticky.id, !isPinned);
    }
  };

  // Handle sticky deletion
  const handleDelete = async () => {
    try {
      if (!sticky.note_id) return;
      setIsDeleteModalOpen(false);
      await removeSticky(sticky.id);
    } catch (error) {
      console.error('Error deleting sticky:', error);
    }
  };

  // Load checklist items when component mounts
  useEffect(() => {
    if (sticky.id && sticky.note_id) {
      fetchChecklistItems();
    }
  }, [sticky.id, sticky.note_id]);

  // Handle Enter key press in input field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addChecklistItem();
    }
  };

  return (
    <div
      className={`sticky checklist-sticky ${isMinimized ? 'minimized' : ''}`}
      style={{
        top: isInMainPanel ? sticky.main_y : sticky.y,
        left: isInMainPanel ? sticky.main_x : sticky.x,
        width: isInMainPanel ? sticky.main_width : sticky.width,
        height: isInMainPanel ? sticky.main_height : sticky.height,
        zIndex: sticky.zIndex
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (sticky.note_id) {
          onFocus?.();
        }
      }}
    >
      <div className="drag-bar" onMouseDown={startDrag} onDoubleClick={toggleMinimize}>
        <div className="sticky-pin">
          <input 
            type="checkbox"
            checked={isPinned}
            onChange={handlePinToggle}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            title="Pin sticky (prevent auto-minimize)"
          />
        </div>
        <div className="sticky-content-preview">
          {isMinimized ? `Checklist (${checklistItems.length} items)` : 'Checklist'}
        </div>
        <div className="sticky-controls">
          <button onClick={(e) => {
            e.stopPropagation();
            toggleExpand();
          }}>
            {isExpanded ? '⊙' : '⊕'}
          </button>
          <button onClick={(e) => {
              e.stopPropagation();
              onToggleDisplayMode(sticky.id, 'list');
            }} title="Send to List">
              ▼
            </button>

          <button onClick={() => setIsDeleteModalOpen(true)}>✖</button>
        </div>
      </div>
      
      {!isMinimized && (
        <>
          <div className="add-item-container">
            <input
              type="text"
              className="add-item-input"
              placeholder="Add new item..."
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button className="add-item-button" onClick={addChecklistItem}>+</button>
          </div>
          
          <div className="checklist-container">
            {isLoading ? (
              <div className="loading-message">Loading items...</div>
            ) : (
              checklistItems.map((item) => (
                <div
                  key={item.id}
                  className={`checklist-item ${item.completed ? 'checklist-item-completed' : ''}`}
                >
                  <div className="checklist-item-handle">↕</div>
                  <input
                    type="checkbox"
                    className="checklist-item-checkbox"
                    checked={item.completed}
                    onChange={() => toggleItemCompleted(item.id)}
                  />
                  <div className="checklist-item-text">{item.text}</div>
                </div>
              ))
            )}
          </div>
          <div className="resize-handle" onMouseDown={startResize} />
        </>
      )}
      
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        className="modal-content"
        overlayClassName="modal-overlay"
        style={{
            overlay: {
              zIndex: 99999, // Very high z-index to ensure it's on top of everything
            },
            content: {
              zIndex: 100000 // Even higher z-index for the content
            }
          }}
      >
        <h3>Delete Checklist Note?</h3>
        <p>Are you sure you want to delete this checklist note?</p>
        <div className="modal-actions">
          <button onClick={handleDelete}>Delete</button>
          <button onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
        </div>
      </Modal>
    </div>
  );
};

export default React.memo(ChecklistSticky);